import React, { useState } from 'react';
import { FilterBar } from '../components/FilterBar';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { getDefaultProgram, areaStudyOptions } from '../data/filterOptions';
import { articles, Article } from '../data/articlesData';
import { useMetaTags } from '../hooks/useMetaTags';
import { ClockIcon, UserIcon, CalendarIcon, ArrowRightIcon, BookOpenIcon, SearchIcon } from 'lucide-react';
interface ArticlesPageProps {
  onNavigate: (page: string, articleSlug?: string) => void;
}
export function ArticlesPage({
  onNavigate
}: ArticlesPageProps) {
  const [degreeLevel, setDegreeLevel] = useState<number>(3);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(0); // 0 = all
  const [selectedProgram, setSelectedProgram] = useState<number>(100);
  // Meta tags
  useMetaTags({
    title: 'Articles & Guides for Online Students | DegreeCompare',
    description: 'Expert advice on choosing the right online program, financial aid, career planning, and more. Resources for prospective and current online students.',
    keywords: 'online education articles, college guides, financial aid tips, career planning, online degree advice, online learning resources',
    ogTitle: 'Articles & Guides for Online Students',
    ogDescription: 'Expert advice and resources for choosing the right online degree program, managing financial aid, and planning your career.'
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  // Filter articles: 0 = show all, otherwise match areaStudyId or show general (0) articles
  const filteredArticles = selectedAreaId === 0 ? articles : articles.filter(a => a.areaStudyId === selectedAreaId || a.areaStudyId === 0);
  const areaName = selectedAreaId === 0 ? 'All Areas of Study' : areaOfStudyData[selectedAreaId]?.name || 'All Areas';
  return <div data-id="element-435">
      {/* Hero */}
      <section className="w-full bg-[#1a2332] text-white py-10 md:py-14 px-4 relative overflow-hidden" data-id="element-436">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-437"></div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-438">
          <div className="flex items-center gap-3 mb-4" data-id="element-439">
            <div className="bg-white/10 p-2 rounded-lg" data-id="element-440">
              <BookOpenIcon className="w-6 h-6" data-id="element-441" />
            </div>
            <span className="text-sm font-medium text-gray-300 uppercase tracking-wider" data-id="element-442">
              Resource Center
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3" data-id="element-443">
            Articles & Guides
          </h1>
          <p className="text-base text-gray-300 max-w-xl" data-id="element-444">
            Expert advice on choosing the right program, financial aid, career
            planning, and more.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="w-full bg-white border-b border-gray-200 py-4 px-4 shadow-sm relative z-30" data-id="element-445">
        <div className="max-w-7xl mx-auto" data-id="element-446">
          <div className="flex flex-col sm:flex-row gap-3 w-full" data-id="element-447">
            {/* Custom "All" area selector that includes an "All" option */}
            <div className="flex flex-col sm:flex-row gap-3 w-full" data-id="element-448">
              <div className="relative flex-1 min-w-0" data-id="element-449">
                <label className="absolute -top-2 left-3 bg-white px-1.5 text-[10px] font-semibold text-[#3b82f6] z-10 uppercase tracking-wider" data-id="element-450">
                  Area of Study
                </label>
                <select value={selectedAreaId} onChange={e => handleAreaChange(Number(e.target.value))} className="w-full bg-white border-2 border-gray-200 rounded-lg py-2.5 px-3 text-sm font-medium text-gray-900 focus:outline-none focus:border-[#3b82f6] transition-colors appearance-none cursor-pointer" data-id="element-451">
                  <option value={0} data-id="element-452">All Areas of Study</option>
                  {areaStudyOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-453">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-10" data-id="element-454">
        <div className="flex items-center justify-between mb-6" data-id="element-455">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-456">
            {filteredArticles.length} article
            {filteredArticles.length !== 1 ? 's' : ''}
            {selectedAreaId !== 0 && <span className="text-gray-500 font-normal" data-id="element-457"> in {areaName}</span>}
          </h2>
        </div>

        {filteredArticles.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center" data-id="element-458">
            <SearchIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-459" />
            <p className="text-gray-600 font-medium" data-id="element-460">
              No articles found for this area of study.
            </p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-461">
              Try selecting a different category or view all articles.
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-462">
            {filteredArticles.map(article => <ArticleCard key={article.id} article={article} onNavigate={onNavigate} data-id="element-463" />)}
          </div>}
      </div>
    </div>;
}
function ArticleCard({
  article,
  onNavigate
}: {
  article: Article;
  onNavigate: (page: string, articleSlug?: string) => void;
}) {
  // Generate a color based on area
  const colorMap: Record<number, string> = {
    0: 'bg-gray-100 text-gray-700',
    1: 'bg-blue-50 text-blue-700',
    2: 'bg-red-50 text-red-700',
    3: 'bg-purple-50 text-purple-700',
    4: 'bg-cyan-50 text-cyan-700',
    5: 'bg-green-50 text-green-700',
    6: 'bg-amber-50 text-amber-700',
    7: 'bg-orange-50 text-orange-700',
    8: 'bg-indigo-50 text-indigo-700',
    9: 'bg-gray-100 text-gray-700',
    10: 'bg-teal-50 text-teal-700',
    11: 'bg-pink-50 text-pink-700',
    12: 'bg-violet-50 text-violet-700',
    13: 'bg-yellow-50 text-yellow-700'
  };
  const badgeColor = colorMap[article.areaStudyId] || colorMap[0];
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // For now, only the business degree article exists
    if (article.id === 'b1') {
      onNavigate('articles', 'online-business-degree-worth-it-2026');
    }
  };
  return <a href="#" onClick={handleClick} className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow flex flex-col cursor-pointer" data-id="element-464">
      {/* Color bar top */}
      <div className="h-1.5 bg-[#1a2332] group-hover:bg-[#e8594f] transition-colors" data-id="element-465"></div>

      <div className="p-5 flex flex-col flex-grow" data-id="element-466">
        <div className="flex items-center gap-2 mb-3" data-id="element-467">
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badgeColor}`} data-id="element-468">
            {article.category}
          </span>
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-[#3b82f6] transition-colors line-clamp-2" data-id="element-469">
          {article.title}
        </h3>

        <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-470">
          {article.excerpt}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto" data-id="element-471">
          <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-472">
            <span className="flex items-center gap-1" data-id="element-473">
              <CalendarIcon className="w-3 h-3" data-id="element-474" />
              {article.date}
            </span>
            <span className="flex items-center gap-1" data-id="element-475">
              <ClockIcon className="w-3 h-3" data-id="element-476" />
              {article.readTime}
            </span>
          </div>
          <span className="text-xs font-semibold text-[#e8594f] flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-477">
            Read <ArrowRightIcon className="w-3 h-3" data-id="element-478" />
          </span>
        </div>
      </div>
    </a>;
}